// TanStack Query hooks for the Personnel & Credentials module. A Personnel
// record is a guard/agent/staff profile that owns several credential
// sub-resources (licenses, certifications, firearms qualifications [sensitive],
// clearances [sensitive], trainings, and recurring availability windows). The
// expiring-credentials report is a flattened roll-up across all credential
// types. Mirrors packages/api/src/routes/personnel.ts.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Certification,
  CertificationResponse,
  CertificationsListResponse,
  Clearance,
  ClearanceResponse,
  ClearancesListResponse,
  CreateCertificationInput,
  CreateClearanceInput,
  CreateFirearmsQualificationInput,
  CreateLicenseInput,
  CreatePersonnelAvailabilityInput,
  CreatePersonnelInput,
  CreateTrainingInput,
  ExpiringCredentialsResponse,
  FirearmsQualification,
  FirearmsQualificationResponse,
  FirearmsQualificationsListResponse,
  License,
  LicenseResponse,
  LicensesListResponse,
  Personnel,
  PersonnelAvailability,
  PersonnelAvailabilityListResponse,
  PersonnelAvailabilityResponse,
  PersonnelClassification,
  PersonnelListResponse,
  PersonnelResponse,
  PersonnelStatus,
  Training,
  TrainingResponse,
  TrainingsListResponse,
  UpdateCertificationInput,
  UpdateClearanceInput,
  UpdateFirearmsQualificationInput,
  UpdateLicenseInput,
  UpdatePersonnelAvailabilityInput,
  UpdatePersonnelInput,
  UpdateTrainingInput,
} from '@dsr/shared-types';
import { api } from '../client';

// ---------- Query keys ----------

const KEY = 'personnel';

export const personnelKeys = {
  all: [KEY] as const,
  lists: () => [KEY, 'list'] as const,
  list: (filters: PersonnelFilters) => [KEY, 'list', { ...filters }] as const,
  detail: (id: number) => [KEY, 'detail', id] as const,
  // Nested credential collections hang off the personnel detail subtree so a
  // single invalidate of detail(id) refreshes every tab for that person.
  sub: (personnelId: number, seg: string) =>
    [KEY, 'detail', personnelId, seg] as const,
  expiring: (withinDays: number) => [KEY, 'expiring', withinDays] as const,
};

// =============================================================================
// Personnel (core)
// =============================================================================

export interface PersonnelFilters {
  status?: PersonnelStatus;
  classification?: PersonnelClassification;
  user_id?: number;
  q?: string;
}

export function usePersonnelList(filters: PersonnelFilters = {}) {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.classification) params.set('classification', filters.classification);
  if (filters.user_id !== undefined) params.set('user_id', String(filters.user_id));
  if (filters.q && filters.q.trim() !== '') params.set('q', filters.q.trim());
  const qs = params.toString();
  const url = qs.length > 0 ? `/api/personnel?${qs}` : '/api/personnel';
  return useQuery({
    queryKey: personnelKeys.list(filters),
    queryFn: ({ signal }) => api.get<PersonnelListResponse>(url, signal),
  });
}

export function usePersonnel(id: number | null) {
  return useQuery({
    queryKey: personnelKeys.detail(id ?? 0),
    enabled: id !== null && id > 0,
    queryFn: ({ signal }) =>
      api.get<PersonnelResponse>(`/api/personnel/${id}`, signal),
  });
}

export function useCreatePersonnel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePersonnelInput) =>
      api.post<PersonnelResponse>('/api/personnel', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: personnelKeys.lists() });
    },
  });
}

export interface UpdatePersonnelArgs extends UpdatePersonnelInput {
  id: number;
}

export function useUpdatePersonnel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdatePersonnelArgs) =>
      api.patch<PersonnelResponse>(`/api/personnel/${id}`, patch),
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: personnelKeys.lists() });
      void qc.invalidateQueries({ queryKey: personnelKeys.detail(id) });
    },
  });
}

export function useDeletePersonnel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/personnel/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: personnelKeys.lists() });
    },
  });
}

// =============================================================================
// Nested credential sub-resources
// =============================================================================
//
// Every credential type is a child of personnel with an identical CRUD shape:
//   GET    /api/personnel/:id/<seg>
//   POST   /api/personnel/:id/<seg>
//   PATCH  /api/personnel/:id/<seg>/:childId
//   DELETE /api/personnel/:id/<seg>/:childId
//
// The differences are data-only (segment, response keys, types), so the hooks
// are produced by a small factory instead of being hand-copied per credential.

interface SubResourceShape<List, Item, Create, Update> {
  /** URL segment, e.g. 'licenses'. */
  segment: string;
  /** Response key for the list payload, e.g. 'licenses'. */
  listKey: string;
  /** Phantom marker so TS infers the four type params from the call site. */
  _list?: List;
  _item?: Item;
  _create?: Create;
  _update?: Update;
}

interface SubResourceHooks<List, Item, Create, Update> {
  useList: (personnelId: number | null, enabled?: boolean) => ReturnType<typeof useQuery<List>>;
  useCreate: () => ReturnType<typeof useMutation<Item, Error, { personnelId: number; input: Create }>>;
  useUpdate: () => ReturnType<
    typeof useMutation<Item, Error, { personnelId: number; childId: number; patch: Update }>
  >;
  useDelete: () => ReturnType<typeof useMutation<void, Error, { personnelId: number; childId: number }>>;
}

function makeSubResource<List, Item, Create, Update>(
  cfg: SubResourceShape<List, Item, Create, Update>,
): SubResourceHooks<List, Item, Create, Update> {
  const { segment } = cfg;

  function useList(personnelId: number | null, enabled = true) {
    return useQuery<List>({
      queryKey: personnelKeys.sub(personnelId ?? 0, segment),
      enabled: enabled && personnelId !== null && personnelId > 0,
      queryFn: ({ signal }) =>
        api.get<List>(`/api/personnel/${personnelId}/${segment}`, signal),
    });
  }

  function useCreate() {
    const qc = useQueryClient();
    return useMutation<Item, Error, { personnelId: number; input: Create }>({
      mutationFn: ({ personnelId, input }) =>
        api.post<Item>(`/api/personnel/${personnelId}/${segment}`, input),
      onSuccess: (_data, { personnelId }) => {
        void qc.invalidateQueries({ queryKey: personnelKeys.sub(personnelId, segment) });
      },
    });
  }

  function useUpdate() {
    const qc = useQueryClient();
    return useMutation<Item, Error, { personnelId: number; childId: number; patch: Update }>({
      mutationFn: ({ personnelId, childId, patch }) =>
        api.patch<Item>(`/api/personnel/${personnelId}/${segment}/${childId}`, patch),
      onSuccess: (_data, { personnelId }) => {
        void qc.invalidateQueries({ queryKey: personnelKeys.sub(personnelId, segment) });
      },
    });
  }

  function useDelete() {
    const qc = useQueryClient();
    return useMutation<void, Error, { personnelId: number; childId: number }>({
      mutationFn: ({ personnelId, childId }) =>
        api.delete<void>(`/api/personnel/${personnelId}/${segment}/${childId}`),
      onSuccess: (_data, { personnelId }) => {
        void qc.invalidateQueries({ queryKey: personnelKeys.sub(personnelId, segment) });
      },
    });
  }

  return { useList, useCreate, useUpdate, useDelete };
}

// ---------- Licenses (credential.read / credential.write) ----------

const licenses = makeSubResource<
  LicensesListResponse,
  LicenseResponse,
  CreateLicenseInput,
  UpdateLicenseInput
>({ segment: 'licenses', listKey: 'licenses' });

export const useLicenses = licenses.useList;
export const useCreateLicense = licenses.useCreate;
export const useUpdateLicense = licenses.useUpdate;
export const useDeleteLicense = licenses.useDelete;

// ---------- Certifications (credential.read / credential.write) ----------

const certifications = makeSubResource<
  CertificationsListResponse,
  CertificationResponse,
  CreateCertificationInput,
  UpdateCertificationInput
>({ segment: 'certifications', listKey: 'certifications' });

export const useCertifications = certifications.useList;
export const useCreateCertification = certifications.useCreate;
export const useUpdateCertification = certifications.useUpdate;
export const useDeleteCertification = certifications.useDelete;

// ---------- Firearms qualifications (SENSITIVE: firearm.read / firearm.write) ----------

const firearmsQuals = makeSubResource<
  FirearmsQualificationsListResponse,
  FirearmsQualificationResponse,
  CreateFirearmsQualificationInput,
  UpdateFirearmsQualificationInput
>({ segment: 'firearms-quals', listKey: 'firearms_qualifications' });

export const useFirearmsQualifications = firearmsQuals.useList;
export const useCreateFirearmsQualification = firearmsQuals.useCreate;
export const useUpdateFirearmsQualification = firearmsQuals.useUpdate;
export const useDeleteFirearmsQualification = firearmsQuals.useDelete;

// ---------- Clearances (SENSITIVE: credential.read / credential.write) ----------

const clearances = makeSubResource<
  ClearancesListResponse,
  ClearanceResponse,
  CreateClearanceInput,
  UpdateClearanceInput
>({ segment: 'clearances', listKey: 'clearances' });

export const useClearances = clearances.useList;
export const useCreateClearance = clearances.useCreate;
export const useUpdateClearance = clearances.useUpdate;
export const useDeleteClearance = clearances.useDelete;

// ---------- Trainings (credential.read / credential.write) ----------

const trainings = makeSubResource<
  TrainingsListResponse,
  TrainingResponse,
  CreateTrainingInput,
  UpdateTrainingInput
>({ segment: 'trainings', listKey: 'trainings' });

export const useTrainings = trainings.useList;
export const useCreateTraining = trainings.useCreate;
export const useUpdateTraining = trainings.useUpdate;
export const useDeleteTraining = trainings.useDelete;

// ---------- Availability (credential.read / credential.write) ----------

const availability = makeSubResource<
  PersonnelAvailabilityListResponse,
  PersonnelAvailabilityResponse,
  CreatePersonnelAvailabilityInput,
  UpdatePersonnelAvailabilityInput
>({ segment: 'availability', listKey: 'availability' });

export const useAvailability = availability.useList;
export const useCreateAvailability = availability.useCreate;
export const useUpdateAvailability = availability.useUpdate;
export const useDeleteAvailability = availability.useDelete;

// =============================================================================
// Expiring credentials report
// =============================================================================
//
// Flattened roll-up across licenses, certifications, clearances, and firearms
// quals whose expires_at falls within `withinDays`. Gated server-side on
// credential.read (the report intentionally includes firearms quals as a
// dashboard summary, not the sensitive detail view).

export function useExpiringCredentials(withinDays: number) {
  return useQuery({
    queryKey: personnelKeys.expiring(withinDays),
    queryFn: ({ signal }) =>
      api.get<ExpiringCredentialsResponse>(
        `/api/personnel/expiring?within_days=${withinDays}`,
        signal,
      ),
  });
}

// Re-export the wire types pages commonly need alongside the hooks.
export type {
  Certification,
  Clearance,
  FirearmsQualification,
  License,
  Personnel,
  PersonnelAvailability,
  PersonnelClassification,
  PersonnelStatus,
  Training,
};
